import React, { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, Clock, Shield } from 'lucide-react';
import { getSecurityEvents, resolveSecurityEvent } from '../../../services/complianceService';
import Button from '../../../components/ui/Button';

/**
 * Security Monitoring Panel Component
 * Shows security events with automated detection and alerting
 */
const SecurityMonitoringPanel = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ severity: '', resolved: false });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  
  useEffect(() => {
    loadEvents();
  }, [filter]);
  
  const loadEvents = async () => {
    setLoading(true);
    const result = await getSecurityEvents(filter);
    if (result?.success) {
      setEvents(result?.data || []);
    }
    setLoading(false);
  };
  
  const handleResolve = async () => {
    if (!selectedEvent || !resolutionNotes) {
      alert('Please provide resolution notes');
      return;
    }
    
    const result = await resolveSecurityEvent(selectedEvent?.id, resolutionNotes);
    if (result?.success) {
      alert('Security event resolved');
      setSelectedEvent(null);
      setResolutionNotes('');
      loadEvents();
    } else {
      alert(`Failed to resolve: ${result?.error}`);
    }
  };
  
  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-100 text-red-800 border-red-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-blue-100 text-blue-800 border-blue-200'
    };
    return colors?.[severity] || colors?.medium;
  };
  
  const getSeverityIcon = (severity) => {
    if (severity === 'critical' || severity === 'high') return AlertTriangle;
    if (severity === 'medium') return Clock;
    return Shield;
  };
  
  const getEventTypeLabel = (type) => {
    const labels = {
      failed_login: 'Failed Login Attempt',
      permission_change: 'Permission Change',
      data_export: 'Data Export',
      unusual_access: 'Unusual Access Pattern',
      brute_force: 'Brute Force Attack',
      privilege_escalation: 'Privilege Escalation',
      unauthorized_phi_access: 'Unauthorized PHI Access'
    };
    return labels?.[type] || type;
  };
  
  const stats = {
    total: events?.length,
    critical: events?.filter(e => e?.severity === 'critical')?.length,
    high: events?.filter(e => e?.severity === 'high')?.length,
    unresolved: events?.filter(e => !e?.resolved)?.length
  };
  
  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="text-sm text-gray-600 mb-1">Total Events</div>
          <div className="text-3xl font-bold text-gray-900">{stats?.total}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="text-sm text-gray-600 mb-1">Critical</div>
          <div className="text-3xl font-bold text-red-600">{stats?.critical}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="text-sm text-gray-600 mb-1">High Severity</div>
          <div className="text-3xl font-bold text-orange-600">{stats?.high}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="text-sm text-gray-600 mb-1">Unresolved</div>
          <div className="text-3xl font-bold text-yellow-600">{stats?.unresolved}</div>
        </div>
      </div>
      
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex gap-4">
          <select
            value={filter?.severity}
            onChange={(e) => setFilter({ ...filter, severity: e?.target?.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="">All Severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          
          <select
            value={filter?.resolved}
            onChange={(e) => setFilter({ ...filter, resolved: e?.target?.value === 'true' })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="false">Unresolved Only</option>
            <option value="true">Resolved Only</option>
          </select>
        </div>
      </div>
      
      {/* Events List */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading events...</div>
        ) : events?.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No security events found</div>
        ) : (
          <div className="divide-y divide-gray-200">
            {events?.map(event => {
              const SeverityIcon = getSeverityIcon(event?.severity);
              
              return (
                <div key={event?.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg border ${getSeverityColor(event?.severity)}`}>
                      <SeverityIcon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-gray-900">{getEventTypeLabel(event?.event_type)}</h4>
                          <p className="text-sm text-gray-600 mt-1">{event?.description}</p>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getSeverityColor(event?.severity)}`}>
                            {event?.severity?.toUpperCase()}
                          </span>
                          {event?.resolved ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle className="w-3 h-3" />
                              Resolved
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              <Clock className="w-3 h-3" />
                              Pending
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="mt-2 text-sm text-gray-500">
                        <span>Detected: {new Date(event?.created_at)?.toLocaleString()}</span>
                        {event?.auto_escalated && (
                          <span className="ml-4 text-orange-600 font-medium">Auto-escalated</span>
                        )}
                        {event?.admin_notified && (
                          <span className="ml-4 text-blue-600">Admin notified</span>
                        )}
                      </div>
                      
                      {event?.resolved && event?.resolution_notes && (
                        <div className="mt-3 text-sm text-gray-600 bg-green-50 rounded p-3">
                          <strong>Resolution:</strong> {event?.resolution_notes}
                          <div className="mt-1 text-xs text-gray-500">
                            Resolved by admin on {new Date(event?.resolved_at)?.toLocaleString()}
                          </div>
                        </div>
                      )}
                      
                      {!event?.resolved && (
                        <div className="mt-3">
                          <Button
                            onClick={() => setSelectedEvent(event)}
                            size="sm"
                            variant="outline"
                          >
                            Resolve Event
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Resolution Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Resolve Security Event</h3>
            
            <div className="mb-4">
              <div className="text-sm text-gray-600 mb-2">
                <strong>Event:</strong> {getEventTypeLabel(selectedEvent?.event_type)}
              </div>
              <div className="text-sm text-gray-600 mb-4">
                <strong>Description:</strong> {selectedEvent?.description}
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resolution Notes:
              </label>
              <textarea
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e?.target?.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Describe how this security event was resolved..."
              />
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setSelectedEvent(null);
                  setResolutionNotes('');
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleResolve}
                className="flex-1"
                disabled={!resolutionNotes}
              >
                Mark as Resolved
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecurityMonitoringPanel;